import { useCallback, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Config from "../../../Config";
import { updateStudent } from "../../../store/StudentSlice";
import { updateUser } from "../../../store/UserSlice";
import { isLessonPast } from "../../../utilities/lessonHistoryUtils";
import { useI18n } from "../../../i18n/I18nProvider";

export const useScheduleLessonModals = ({
  timeZone,
  schedule,
  student,
  user,
  dispatch,
  loadData,
}) => {
  const { t } = useI18n();
  const [isShowModalNote, setIsShowModalNote] = useState(false);
  const [isShowModalReschedule, setIsShowModalReschedule] = useState(false);
  const [lessonForModal, setLessonForModal] = useState({});
  const [modalSuccessText, setModalSuccessText] = useState("");
  const [modalAlertText, setModalAlertText] = useState("");
  const [modalErrorText, setModalErrorText] = useState("");

  const getStudent = async (studentId) => {
    try {
      const res = await axios.get(Config.BACKEND_ADDR + `/students/${studentId}`);
      const downloadedData = res.data;
      dispatch(
        updateStudent({
          studentId: downloadedData.id,
          lessonPrice: downloadedData.lesson_cost,
          balance: downloadedData.balance,
          age: downloadedData.age,
          image: downloadedData.image
            ? Config.CLOUD_ADDR + "/" + downloadedData.image
            : process.env.PUBLIC_URL + "/no_student_icon.png",
          payedLessons: parseInt(
            downloadedData.balance / downloadedData.lesson_cost,
          ),
          name: downloadedData.name,
          englishLevel: downloadedData.english_skill,
          additionalInfo: downloadedData.comment,
        }),
      );
    } catch (error) {
      console.error("Error fetching student data:", error);
    }
  };

  const memoOpenModalNote = useCallback((lesson) => {
    getStudent(lesson.studentId);
    setLessonForModal(lesson);
    setIsShowModalNote(true);
  }, []);

  const memoOpenModalReschedule = useCallback(
    (lesson) => {
      if (isLessonPast(lesson, timeZone)) {
        memoOpenModalNote(lesson);
        return;
      }
      getStudent(lesson.studentId);
      setLessonForModal(lesson);
      setIsShowModalReschedule(true);
    },
    [timeZone, memoOpenModalNote],
  );

  const memoCloseModalNote = useCallback(() => {
    setLessonForModal({});
    setIsShowModalNote(false);
  }, []);

  const memoCloseModalReschedule = useCallback(() => {
    setLessonForModal({});
    setIsShowModalReschedule(false);
  }, []);

  const memoSaveNote = async (comment, commentOption, lessonData) => {
    const updateLesson = async (lessonIdForUpd) => {
      try {
        return await axios.patch(Config.BACKEND_ADDR + `/lessons/note/`, {
          lesson_id: lessonIdForUpd,
          note: comment,
        });
      } catch (error) {
        return error;
      }
    };

    const successAction = async () => {
      setLessonForModal({});
      await loadData();
      setModalSuccessText(t("Заметка добавлена"));
    };

    if (commentOption === "lesson") {
      try {
        const resUpd = await updateLesson(lessonData.id);
        if (resUpd.status === 200) {
          await successAction();
        } else {
          console.error("Error updating lesson:", resUpd.data);
          setModalErrorText(t("Ошибка обновления урока"));
        }
      } catch (error) {
        console.error("Error updating lesson:", error);
      } finally {
        return true;
      }
    }

    const numberDays = commentOption === "week" ? 7 : moment().daysInMonth();
    const lessonsForUpdate = schedule.filter((el) => {
      const lessonDate = moment(el.dateDate).tz(timeZone);
      const startDate = moment(lessonData.dateDate).tz(timeZone);
      const endDate = moment(lessonData.dateDate)
        .tz(timeZone)
        .add(numberDays - 1, "days")
        .endOf("day");
      return (
        lessonDate.isSameOrAfter(startDate) &&
        lessonDate.isSameOrBefore(endDate) &&
        el.studentId === lessonData.studentId
      );
    });
    try {
      for (const lesson of lessonsForUpdate) {
        const resUpd = await updateLesson(lesson.id);
        if (resUpd.status === 200) {
          console.log("Заметка добавлена на ", lesson.id, resUpd.data);
        } else {
          console.error("Error updating lesson:", resUpd.data);
          setModalErrorText(t("Ошибка обновления урока"));
        }
      }
      await successAction();
    } catch (error) {
      console.error("Error updating lesson:", error);
    } finally {
      return true;
    }
  };

  const memoSaveDateTime = async (dateTimeUTC, isDelete, isPaid, lessonData) => {
    if (isLessonPast(lessonData, timeZone)) {
      setModalErrorText(t("Нельзя изменять прошедший урок"));
      return false;
    }

    await getStudent(lessonData.studentId);

    const [startHour, startMinute] = (
      lessonData.timeSlot.split(/\s*-\s*/)[0] || ""
    )
      .split(":")
      .map(Number);
    const [endHour, endMinute] = (lessonData.timeSlot.split(/\s*-\s*/)[1] || "")
      .split(":")
      .map(Number);
    const intervalMinutes =
      endHour * 60 + endMinute - (startHour * 60 + startMinute);
    const endDateTimeUTC = dateTimeUTC + intervalMinutes * 60;

    const handleDatetimeError = (error) => {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409) {
        setModalAlertText(
          t("У вас уже есть уроки в это время ( {{date}} ). Пожалуйста, проверьте расписание и выберите другой слот.", { date: moment.utc(detail).tz(timeZone).format("DD.MM.YYYY HH:mm") }),
        );
        return;
      }
      if (status === 400 && typeof detail === "string") {
        setModalErrorText(detail);
        return;
      }
      if (status >= 500) {
        setModalErrorText(t("Ошибка сервера, попробуйте ещё раз"));
        return;
      }
      setModalErrorText(t("Ошибка обновления урока"));
    };

    const successAction = async (text) => {
      setLessonForModal({});
      await loadData();
      setModalSuccessText(text ? text : t("Изменения сохранены"));
    };

    if (isDelete) {
      try {
        const resDelete = await axios.delete(
          Config.BACKEND_ADDR + `/lessons/${lessonData.id}`,
        );
        if (resDelete.status !== 200) {
          setModalErrorText(t("Ошибка удаления урока"));
          return false;
        }
        setModalAlertText(
          t("Вы удаляете только текущий урок. Если вам нужно внести постоянные изменения в расписание, это можно сделать в карточке ученика."),
        );
        await successAction(t("Занятие удалено"));

        if (isPaid) {
          const countOfHalfHours = Math.floor(intervalMinutes / 30);
          const paySum = +lessonData.cost * countOfHalfHours;
          const newBalance = +student.balance - paySum;
          const resUpdStudent = await axios.patch(
            Config.BACKEND_ADDR + `/students/balance/`,
            { balance: newBalance, student_id: lessonData.studentId },
          );
          if (resUpdStudent.status === 200) {
            dispatch(
              updateStudent({
                studentId: lessonData.studentId,
                balance: newBalance,
              }),
            );
          } else {
            setModalErrorText(t("Ошибка обновления баланса ученика"));
          }

          const userBalance = user.balance ? user.balance : 0;
          const newTeacherBalance = +userBalance + +paySum;
          const resUpdTeacher = await axios.patch(
            Config.BACKEND_ADDR + `/auth/teacher/balance`,
            { balance: newTeacherBalance },
          );
          if (resUpdTeacher.status === 200) {
            dispatch(updateUser({ balance: newTeacherBalance }));
          } else {
            setModalErrorText(t("Ошибка обновления баланса учителя"));
          }
        }
        return true;
      } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        if (status === 400 && typeof detail === "string") {
          setModalErrorText(detail);
        } else if (status >= 500) {
          setModalErrorText(t("Ошибка сервера, попробуйте ещё раз"));
        } else {
          setModalErrorText(t("Ошибка удаления урока"));
        }
        return false;
      }
    }

    try {
      const resUpd = await axios.patch(
        Config.BACKEND_ADDR + `/lessons/datetime/`,
        {
          lesson_id: lessonData.id,
          start_time: dateTimeUTC,
          end_time: endDateTimeUTC,
        },
      );
      if (resUpd.status === 200) {
        setModalAlertText(
          t("Перенос произойдет однократно. Если вам нужно внести постоянные изменения в расписание, это можно сделать в карточке ученика."),
        );
        await successAction();
        return true;
      }
      setModalErrorText(t("Ошибка обновления урока"));
      return false;
    } catch (error) {
      handleDatetimeError(error);
      return false;
    }
  };

  return {
    isShowModalNote,
    isShowModalReschedule,
    lessonForModal,
    modalSuccessText,
    setModalSuccessText,
    modalAlertText,
    setModalAlertText,
    modalErrorText,
    setModalErrorText,
    memoOpenModalNote,
    memoOpenModalReschedule,
    memoCloseModalNote,
    memoCloseModalReschedule,
    memoSaveNote,
    memoSaveDateTime,
  };
};
